import dayjs from "dayjs";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { supabase } from "../../../config/supabaseClient";
import { useCallback, useMemo, useRef } from "react";
import {
  getAllDatesFromSelection,
  reactQueryParams,
} from "../../StayUpToDate/RenderComponent";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { StaticVariables } from "../../StaticVariables";

dayjs.extend(utc);
dayjs.extend(timezone);

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const POSTS_PER_PAGE = 40;

type ActiveFilter = "both" | "content" | "products" | undefined;

/** Exported for template tab probes — must stay aligned with {@link useLoadYesterdayLinks}. */
export function computeWindow(
  daysNumber: any,
  componentType?: string,
  selectedDays?: string[]
) {
  let start = "";
  let end = "";

  // Daywise (or caller passed selectedDays without componentType) uses explicit dates
  const useSelectedDays =
    (componentType === "daywise" || (selectedDays && selectedDays.length > 0 && daysNumber?.length === 0));
  if (useSelectedDays && selectedDays && selectedDays.length > 0) {
    const dates = getAllDatesFromSelection(selectedDays);
    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates.map((d) => new Date(d).getTime())));
      const maxDate = new Date(Math.max(...dates.map((d) => new Date(d).getTime())));
      start = dayjs(minDate).tz(userTimezone).startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
      end = dayjs(maxDate).tz(userTimezone).endOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
      return { start, end };
    }
    // Fallback: if no valid dates (e.g. unknown day names), use last 7 days so query still runs
    const today = dayjs().tz(userTimezone);
    start = today.clone().subtract(7, "day").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    end = today.endOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    return { start, end };
  }

  if (daysNumber && daysNumber.length > 0) {
    // Always anchor to the start/end of whole days so the query key
    // remains stable while the user navigates within the same day/week.
    const max = Math.max(...daysNumber);
    const today = dayjs().tz(userTimezone);
    const endOfToday = today.endOf("day").utc().format("YYYY-MM-DD HH:mm:ss");

    if (max === 30) {
      start = today
        .clone()
        .subtract(1, "month")
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      end = endOfToday;
    } else if (max === 7) {
      start = today
        .clone()
        .subtract(7, "day")
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      end = endOfToday;
    } else {
      const targetDay = today.clone().subtract(max, "day");
      start = targetDay.startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
      end = targetDay.endOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    }
  }

  return { start, end };
}

export function useLoadYesterdayLinks(
  username: string,
  user_id: string,
  daysNumber: any,
  postIds: any, // kept for signature compatibility; not used by RPC
  removedPostIds: any,
  name: string,
  isChange?: boolean,
  order?: string | boolean,
  selectedDays?: string[],
  componentType?: string,
  customOrderPostIds?: string[],
  activeFilter?: ActiveFilter,
  queryScope: string = "default",
  instanceKey: string = "default",
  enabled: boolean = true
) {
  const queryClient = useQueryClient();

  const processedRemovedPostIds = useMemo<string[]>(() => {
    if (!removedPostIds) return [];
    return Array.from(new Set(removedPostIds.split(",").filter(Boolean)));
  }, [removedPostIds]);

  const socials = useMemo<any[]>(() => {
    if (activeFilter === "content") return StaticVariables().CONTENT_PLATFORMS;
    if (activeFilter === "products") return StaticVariables().PRODUCT_PLATFORMS;
    return StaticVariables().PLATFORMS; // both / default
  }, [activeFilter]);

  const windowRange = useMemo(
    () => computeWindow(daysNumber, componentType, selectedDays),
    [daysNumber, componentType, selectedDays]
  );

  const requestedOrder =
    order === false || order === "false" ? "ASC" : String(order || "DESC");
  const isCustomOrder = requestedOrder.toUpperCase() === "CUSTOM";
  const fetchOrderRef = useRef(isCustomOrder ? "DESC" : requestedOrder);
  if (!isCustomOrder) fetchOrderRef.current = requestedOrder;
  const fetchOrder = isCustomOrder ? fetchOrderRef.current : requestedOrder;
  const customOrderKey = (customOrderPostIds || [])
    .map((id: any) => String(id).trim())
    .filter(Boolean)
    .join(",");

  // Only warn when this hook is actually supposed to fetch. Callers (e.g.
  // useShopLinkIndividualPosts) always invoke the hook for Rules-of-Hooks,
  // passing [] + enabled:false when the look isn't a yesterday-links type —
  // logging then floods the console on every parent re-render.
  if (
    enabled &&
    (!windowRange.start || !windowRange.end)
  ) {
    console.warn("useLoadYesterdayLinks: Empty window range", {
      daysNumber,
      componentType,
      selectedDays,
      range: windowRange,
    });
  }

  // Get selected dates for daywise filtering (for SQL parameter)
  // Returns array of date strings in YYYY-MM-DD format for SQL date[] parameter
  const selectedDatesArray = useMemo<string[] | null>(() => {
    if (selectedDays && selectedDays.length > 0) {
      const dates = getAllDatesFromSelection(selectedDays);
      if (dates.length === 0) return null;
      return dates.map((dateStr) => {
        const date = new Date(dateStr);
        return dayjs(date).tz(userTimezone).format("YYYY-MM-DD");
      });
    }
    return null;
  }, [selectedDays]);

  const getPaginatedFeed = useCallback(async ({ pageParam = 1 } = {}) => {
    const start = performance.now(); 
    
    // Calculate offset for pagination
    const offset = (pageParam - 1) * POSTS_PER_PAGE;
    
    // Convert start_date and end_date from timestamp strings to date strings (YYYY-MM-DD) for SQL
    // windowRange.start and windowRange.end are in format "YYYY-MM-DD HH:mm:ss", extract just date part
    const startDate = windowRange.start ? windowRange.start.split(' ')[0] : null;
    const endDate = windowRange.end ? windowRange.end.split(' ')[0] : null;
    
    // Normalize order: treat boolean/false as ASC to match SQL expectations.
    // CUSTOM is sorted on the client so the preview does not refetch.
    const normalizedOrder = fetchOrder;

    const { data: searchResult, error } = 
    await supabase.rpc("get_shoplink_content", {
      userid: user_id,
      user_name: username,
      start_date: startDate,
      end_date: endDate,
      post_ids: [], // keeping as your proc expects it, but you're not using postIds here
      order_type: normalizedOrder,
      custom_order: isCustomOrder ? [] : customOrderPostIds || [],
      socials,
      limit_num: POSTS_PER_PAGE,
      offset_num: offset,
      // NEW PARAMETERS - all filtering now happens in SQL
      removed_post_ids: processedRemovedPostIds.length > 0 ? processedRemovedPostIds : [],
      component_type: componentType || null,
      selected_dates: selectedDatesArray,
      user_timezone: userTimezone,
    });
    if (error) throw error;
    const t1 = performance.now();
    
    // Extract data, total, and post_ids from RPC response
    // RPC now returns filtered data (removed posts and daywise dates already filtered in SQL)
    const responseData = searchResult?.data || [];
    const totalCount = searchResult?.total || 0;
    const allPostIds = searchResult?.post_ids || [];
    
    // Track original data length (now reflects SQL-filtered data)
    const originalDataLength = responseData.length;
    
    // No client-side filtering needed - SQL handles everything
    // Return paginated structure
    return {
      data: responseData,
      total_count: totalCount, // Use SQL-calculated total that reflects all filters
      originalDataLength: originalDataLength,
      post_ids: allPostIds, // All post IDs from RPC response
    };
  }, [
    user_id,
    username,
    windowRange.start,
    windowRange.end,
    order,
    customOrderPostIds,
    socials,
    componentType,
    selectedDatesArray,
    processedRemovedPostIds,
    userTimezone,
    POSTS_PER_PAGE,
  ]);

  // Stable, string-only query key parts (include new SQL parameters)
  const queryKey = [
    "YesterdayPosts",
    queryScope,
    instanceKey,
    user_id,
    windowRange.start,
    windowRange.end,
    fetchOrder,
    "",
    socials.join("|"),
    processedRemovedPostIds.join("|"),
    name ?? "",
    componentType || "",
    selectedDatesArray ? selectedDatesArray.join("|") : "",
    userTimezone,
  ] as const;

  const isQueryEnabled =
    enabled && Boolean(user_id && windowRange.start && windowRange.end);

  const queryResult = useInfiniteQuery(queryKey, getPaginatedFeed, {
    enabled: isQueryEnabled,
    getNextPageParam: (lastPage: any, allPages: any) => {
      if (!lastPage || !lastPage.data) {
        return undefined;
      }

      /**
       * IMPORTANT:
       * Don't infer "no more pages" from the current page length.
       * In edit flows we optimistically remove items from cached pages, which can
       * make `lastPage.data.length < POSTS_PER_PAGE` even if there are more items.
       *
       * Instead, rely on the SQL-provided `total_count` and the page index.
       */
      const totalCount = Number(lastPage.total_count || 0);
      if (Number.isFinite(totalCount) && totalCount > 0) {
        const loadedCapacity = allPages.length * POSTS_PER_PAGE;
        if (loadedCapacity >= totalCount) {
          return undefined;
        }
        return allPages.length + 1;
      }

      // Fallback (rare): only if total_count is missing.
      const lastLen =
        lastPage.originalDataLength !== undefined
          ? Number(lastPage.originalDataLength)
          : Number(lastPage.data?.length || 0);
      if (lastLen < POSTS_PER_PAGE) return undefined;

      // Otherwise, return the next page number
      return allPages.length + 1;
    },
    keepPreviousData: true,
    // Base React Query params (retry behavior, cache times, etc.)
    ...reactQueryParams(),
    // IMPORTANT: avoid turning every focus change (e.g. modal open/close)
    // into a "full loading" state that replaces content with skeletons.
    refetchOnWindowFocus: false,
  });

  // Transform the result to maintain backward compatibility
  const firstPage = queryResult.data?.pages?.[0];
  const totalCount =
    firstPage && typeof firstPage === "object" && "total_count" in firstPage
      ? firstPage.total_count
      : 0;

  // Get post_ids from first page (RPC returns all post_ids in first page response)
  const allPostIdsFromRPC = 
    firstPage && typeof firstPage === "object" && "post_ids" in firstPage
      ? firstPage.post_ids || []
      : [];

  // Flatten pages into single array for backward compatibility
  const flattenedData = useMemo(() => {
    const rows =
      queryResult.data?.pages?.flatMap((page: any) => page.data || []) || [];
    if (!isCustomOrder || !customOrderKey) return rows;
    const rank = new Map(
      customOrderKey.split(",").map((id, index) => [id, index]),
    );
    return [...rows].sort((a, b) => {
      const left = rank.get(String(a?.post_id ?? ""));
      const right = rank.get(String(b?.post_id ?? ""));
      if (left == null && right == null) return 0;
      if (left == null) return 1;
      if (right == null) return -1;
      return left - right;
    });
  }, [queryResult.data, isCustomOrder, customOrderKey]);

  const forceRefetch = () => {
    queryClient.invalidateQueries(queryKey);
  };

  return {
    ...queryResult,
    data: flattenedData, // Flattened array for backward compatibility
    length: flattenedData.length, // Length of the data array
    post_ids: allPostIdsFromRPC, // All post IDs from RPC response
    dataPages: queryResult.data?.pages || [], // Keep pages structure
    total_count: totalCount, // Use SQL-calculated total that reflects all filters
    isLoading: isCustomOrder
      ? queryResult.isLoading
      : queryResult.isLoading || queryResult.isFetching,
    // Expose more granular loading states for callers that want to distinguish
    // between the first load vs background refetches.
    isInitialLoading: queryResult.isLoading,
    isBackgroundFetching: queryResult.isFetching,
    refetch: queryResult.refetch,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    forceRefetch,
  };
}
